import {FC, useContext, useState} from "react";
import Card from "../../components/ui/Card";
import FlowList from "../../components/admin/flows/FlowList";
import Button from "../../components/ui/Button";
import {FaPlusCircle, FaSave, FaTrash} from "react-icons/fa";
import Input from "../../components/ui/Input";
import {AnimatePresence} from "framer-motion";
import {SubmitHandler, useForm} from "react-hook-form";
import {toast} from "react-hot-toast";
import useApi from "../../hooks/useApi";
import axios from "axios";
import {AdminContext} from "../../context/AdminContext";
import validator from 'validator';
import Modal from "../../components/ui/Modal";
import {IFlow} from "../../types/ApiTypes";
import handleToastError from "../../helpers/handleToastError";

interface Inputs {
    url: string;
    name: string;
}

const Flows: FC = () => {
    const {register, handleSubmit, formState:{errors}, setValue} = useForm<Inputs>();
    const {createFlow,updateFlow,deleteFlow} = useApi();
    const admCtx = useContext(AdminContext);

    // Confirmation modal
    const [confirmationModal, setConfirmationModal] = useState(false);

    // Edit/Create modal
    const [isOpen, setIsOpen] = useState(false);
    const [flowToBeEdited, setFlowToBeEdited] = useState<IFlow>();
    const [editMode, setEditMode] = useState(false);

    const onSubmit: SubmitHandler<Inputs> = async (e) => {
        if(editMode) {
            if(!flowToBeEdited) return toast.error('Kein Flow vorhanden');
            const updatedFlow = await toast.promise(updateFlow(flowToBeEdited.id,{
                newUrl: e.url,
                name: e.name
            }),{
                loading: 'Der Formular wird abgeschickt',
                success: <span className='text-sm'>Das Flow {e.name} wurde bearbeitet</span>,
                error: (err) => handleToastError(err, `Das Flow ${e.name} konnte nicht bearbeitet werden`)
            });
            if(!updatedFlow) return;
            admCtx.dispatch({type: 'UPDATE_FLOW', payload: {updatedFlow: updatedFlow, flowId: flowToBeEdited.id}});
            setEditMode(false);
            setIsOpen(false);
            setFlowToBeEdited(undefined);
            return;
        }
        const flow = await toast.promise(createFlow({
            url: e.url,
            name: e.name
        }),{
            loading: 'Der Formular wird abgeschickt',
            success: <span className='text-sm'>Das Flow {e.name} wurde erstellt</span>,
            error: (err) => handleToastError(err, `Das Flow ${e.name} konnte nicht erstellt werden.`)
        });
        admCtx.dispatch({type: 'APPEND_FLOW', payload: flow});
        setIsOpen(false);
    }

    const onEditSelected = (url:string) => {
        const flow = admCtx.state.flows.find(x => x.url === url);
        if(!flow) return;
        setFlowToBeEdited(flow);
        setValue('url', flow.url);
        setValue('name', flow.name);
        setEditMode(true);
        setIsOpen(true);
    }

    const onDeleteSelected = (url:string) => {
        const flow = admCtx.state.flows.find(x => x.url === url);
        if(!flow) return;
        setFlowToBeEdited(flow);
        setConfirmationModal(true);
    }

    const deleteSelectedFlow = async () => {
        if(!flowToBeEdited) return;
        await toast.promise(deleteFlow(flowToBeEdited.id),{
            loading: 'Das Flow wird gelöscht',
            success: () => {
                const flowName = flowToBeEdited.name;
                admCtx.dispatch({type: 'REMOVE_FLOW', payload: flowToBeEdited.id});
                setConfirmationModal(false);
                setFlowToBeEdited(undefined);
                return <span>Das Flow {flowName} wurde gelöscht</span>
            },
            error: (err) => handleToastError(err, "Das Flow konnte nicht gelöscht werden")
        })
    }

    return (
        <>
            <Card>
                <FlowList flows={admCtx.state.flows} editSelected={onEditSelected} deleteSelected={onDeleteSelected}/>
                <div className='flex justify-between items-center mt-3'>
                    <ul className='flex gap-2'>
                        <li>
                            <Button type='button'>{'<<'}</Button>
                        </li>
                        <li>
                            <Button type='button'>{'<'}</Button>
                        </li>
                        <li>
                            <Button type='button'>{'>'}</Button>
                        </li>
                        <li>
                            <Button type='button'>{'>>'}</Button>
                        </li>
                    </ul>
                    <Button type='button' onClick={() => setIsOpen(true)}><FaPlusCircle className='mr-2'/> Neues Flow
                        erstellen</Button>
                </div>
            </Card>
            <AnimatePresence>
                {isOpen && (
                    <Modal isOpen={isOpen} onClose={() => {setIsOpen(false); setEditMode(false); setValue('name',''); setValue('url','')}} title='Neues Flow erstellen'>
                        <form className='space-y-2' onSubmit={handleSubmit(onSubmit)}>
                            <div>
                                <label htmlFor='flow_url' className='font-semibold'>Flow URL</label>
                                <Input id='flow_url' label='Flow URL' {...register('url',{required: true, validate: value => validator.isURL(value) })}/>
                                {errors.url && <span className='text-xs text-error-brighter'>Dieses Feld ist ein Pflichtfeld und muss eine valide URL sein.</span>}
                            </div>
                            <div>
                                <label htmlFor='flow_name' className='font-semibold'>Name des Flows</label>
                                <Input id='flow_name' label='Flow name' {...register('name',{required: true, minLength: 5})}/>
                                {errors.name && <span className='text-xs text-error-brighter'>Der Name ist ein Pflichtfeld und muss mindestens 5 Zeichen lang sein.</span>}
                            </div>
                            {!editMode && <Button type='submit'><FaPlusCircle className='mr-2'/>Erstellen</Button>}
                            {editMode && <Button type='submit' className='bg-success-darker hover:shadow-success-darker/20'><FaSave className='mr-2'/>Speichern</Button>}
                        </form>
                    </Modal>
                )}
                {confirmationModal && (
                    <Modal isOpen={confirmationModal} onClose={() => {setConfirmationModal(false)}} title='Flow löschen'>
                        Bist du sicher, dass du das Flow löschen möchtest?
                        <div className='flex justify-end gap-4'>
                            <Button onClick={deleteSelectedFlow} type='button' className='bg-error-brighter hover:shadow-error-brighter/20'><FaTrash className='mr-2'/>Ja</Button>
                            <Button onClick={() => setConfirmationModal(false)} type='button'>Nein</Button>
                        </div>
                    </Modal>
                )}
            </AnimatePresence>
        </>
    );
};

export default Flows;